import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums';

import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {getConfigByApp, utilServerSideDeviceDetection} from '@utils';
import {metaInfo} from '@configs';
import {HOME, JOBFEED, RESET_PASSWORD, ROUTE_JOBFEED} from '@constants';

import type {GetServerSidePropsContext} from 'next/types';
import loadSEO from '@helpers/loadSEO';
import SetPasswordWidget from '@jobseeker/feature-authentication/components/SetPassword';
import {SeoIntrup} from '@layouts/SeoLayout';
import {isCSR} from '@jobseeker/lotus/utils';
import {useState} from 'react';
import Dialog from '@jobseeker/lotus/components/Dialog';
import dynamic from 'next/dynamic';
import Loader from '@jobseeker/lotus/components/Loader';
import {openUrl} from '@jobseeker/common-core/utils/common';
import cookie from 'cookie';
import {COOKIE_LOGIN_AUTH} from '@jobseeker/common-core/constants';
import {gladiatorService} from '@jobseeker/common-core/services';
import {getCookiesToDelete} from '@helpers/ssrMap';
import {Box} from '@mui/material';
import {AppState} from '@jobseeker/common-core/types';
import InjectSearchOverlay from '@components/SearchInjection';

const LoginForm = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/AuthenticationHarness/AuthForm'
    ),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);

const HiristAuthentication = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/HiristAuthenticationHarness/AuthForm'
    ),
  {
    ssr: false,
  }
);

type SetPasswordProps = {
  seoConfig: object;
};

const SetPassword = ({seoConfig}: SetPasswordProps) => {
  const metaData = getConfigByApp(metaInfo, JOBFEED, true, 'jobfeed');
  const [loginOpen, setLoginOpen] = useState(false);

  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch);

  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: 'Reset Password',
      item: '',
    },
  ];

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const {searchOverlay, enableLoginPopup} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );

  const onLoginSuccess = () => {
    if (isCSR()) {
      openUrl('/', '_self');
    }
  };

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout seoConfig={seoConfig} paginationEnabled={true} />
        <NavLayout>
          <ResponsiveLayout
            variant={VersionVariants.VERSION_TWO}
            breadcrumbElements={breadcrumbElements}
          >
            {isCSR() && (
              <SeoIntrup pageNo={0} nextPage={false} type={'reset-password'} />
            )}

            <InjectSearchOverlay isSearchEnabled={searchOverlay} />

            <Box mt={isAppIimjobs ? 0 : 7.5} mx={isAppIimjobs ? 0 : 2}>
              <SetPasswordWidget
                handleLoginCTA={() => {
                  setLoginOpen(true);
                }}
              />
            </Box>

            {loginOpen && isAppIimjobs && (
              <Dialog
                title="Login to your account"
                subtitle="Welcome back! 👋"
                invertedTitle
                variant={VersionVariants.VERSION_TWO}
                open={loginOpen}
                onClose={() => setLoginOpen(false)}
              >
                <LoginForm
                  onSuccess={() => {
                    setLoginOpen(false);
                    openUrl(ROUTE_JOBFEED, '_self');
                  }}
                  variant={VersionVariants.VERSION_TWO}
                />
              </Dialog>
            )}

            {!isAppIimjobs && (
              <HiristAuthentication
                variant={VersionVariants.VERSION_TWO}
                onSuccess={onLoginSuccess}
                openDialog={enableLoginPopup || loginOpen}
                setOpenDialog={() => {
                  setLoginOpen(false);
                  dispatch({
                    type: 'AppContext/set_login_popup',
                    payload: false,
                  });
                }}
              />
            )}
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
    </>
  );
};

export const getServerSideProps = wrapper.getStaticProps(
  store => async (context: GetServerSidePropsContext) => {
    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: RESET_PASSWORD},
    });

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    const seoConfig = await loadSEO(HOME);

    try {
      if (context.req?.headers?.cookie) {
        const cookies = cookie.parse(context.req.headers.cookie);
        console.log(
          '🚀 ~ file: index.tsx:40 ~ store=> ~ cookies:',
          context.req?.headers
        );

        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          const validateInfo = await gladiatorService.validate('validate', {
            headers: {
              cookie: context.req?.headers?.cookie,
            },
          });
          console.log(
            '🚀 ~ file: index.tsx:56 ~ store=> ~ validateInfo:',
            validateInfo,
            typeof validateInfo
          );

          const cookiesTodelete = getCookiesToDelete();
          context.res?.setHeader('Set-Cookie', cookiesTodelete);
        }
      }
    } catch (error: /* eslint-disable @typescript-eslint/no-explicit-any */ any) {
      console.log('🚀 ~ file: index.tsx:71 ~ store=> ~ validate error:', error);

      const cookiesTodelete = getCookiesToDelete();
      context.res?.setHeader('Set-Cookie', cookiesTodelete);

      return {
        props: {seoConfig},
      };
    }

    return {
      props: {seoConfig},
    };
  }
);

export default SetPassword;
